import * as React from 'react';
import './App.css'
import { Route, Routes } from 'react-router';
import { useLocation, useNavigate } from 'react-router-dom';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Landing from './components/Landing/Landing';
import Home from './components/Portal/Home';
import { UserProvider } from './components/UserContext';
import CustomerService from './services/CustomerService';
import StoreView from './components/Portal/StoreView/StoreView';
import QuotesView from './components/Portal/QuotesView/QuotesView';
import { SnackbarProvider, useSnackbar } from 'notistack'
import { useMsal } from "@azure/msal-react";
import ConfirmDialog from './components/Common/ConfirmDialog';
import eventEmitter from './components/Common/eventEmitter';
import { LocationOff } from '@mui/icons-material';
import UnderMaintenance from './components/Landing/UnderMaintenance';
import useWebSocket from './components/useWebSocket.jsx';
import ErrorBoundary from './components/ErrorBoundry.jsx';


// overides to the MUI theme
const theme = createTheme({
    typography: {
        fontFamily: [
            'Source Sans Pro',
            'sans-serif', // Fallback to a generic sans-serif if the main font is not available
        ].join(','),
    },
    
});

function App() {
    const { instance, accounts } = useMsal();
    const { enqueueSnackbar } = useSnackbar();
    const request = React.useMemo(() => ({
        scopes: ["openid", "email"],
        prompt: "select_account"
    }), []);

    const ensureActiveAccount = React.useCallback(() => {
        const activeAccount = instance.getActiveAccount();
        if (activeAccount) {
            return activeAccount;
        }

        if (accounts.length > 0) {
            instance.setActiveAccount(accounts[0]);
            return accounts[0];
        }

        return null;
    }, [instance, accounts]);

    let location = useLocation();
    let navigate = useNavigate();
    const [isLoading, setIsLoading] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState('');
    const [loggedInUser, setLoggedInUser] = React.useState(null);
    const [locationUnderMaintenance, setLocationUnderMaintenance] = React.useState(false);

    const [confirmDialog, setConfirmDialog] = React.useState(null);


    const generateJWTToken = React.useCallback(async (idToken) => {
        const postData = {
            idToken: idToken
        };

        try {
            const response = await CustomerService.send('POST', postData, 'Customer/validateMSAL');
            if (response !== null && response.success) {
                const jwtToken = response.token;
                console.log("Received JWT token:", jwtToken);
                localStorage.setItem("CustomerToken", jwtToken);
                setLoggedInUser(response.customer);
                navigate('/portal');
                return true;
            }
        } catch (error) {
            enqueueSnackbar(error?.message ?? 'Unable to complete login.', { variant: 'error' });
        }

        return false;
    }, [enqueueSnackbar, navigate]);

    const checkTokenAndLogin = React.useCallback(async () => {
        const authToken = localStorage.getItem("CustomerToken");
        if (!authToken) {
            return false;
        }

        if (isTokenExpired(authToken)) {
            console.log('Token is expired');
            sessionStorage.clear();
            localStorage.clear();
            return false;
        }

        try {
            const response = await CustomerService.send('GET', '', 'Customer/Me');

            if (response !== null) {
                if (response.result === "SUCCESS" && response.customer !== null) {
                    setLoggedInUser(response.customer);
                    navigate('/portal');
                    return true;
                }

                if (response.message === 'Your Account is not yet verified. Click the link we emailed you.') {
                    setConfirmDialog({
                        show: true,
                        close: handleCloseAccountNotVerified,
                        cancelText: 'Close',
                        title: 'Account Verification Required',
                        content: <div>
                            <div style={{ fontWeight: 'bold', marginBottom: '25px' }}>Your Account is not yet verified. Click the link we emailed you.</div>
                        </div>,
                        data: null
                    });
                    return true;
                }

                if (response.message === 'Under Maintenance') {
                    setLocationUnderMaintenance(true);
                }
            }
        } catch (error) {
            if (error !== null) {
                enqueueSnackbar(error.error !== undefined ? error.error : error.message !== undefined ? error.message : 'An error occurred', { variant: 'error' });
            }
        }

        return false;
    }, [enqueueSnackbar, navigate]);

    const checkAccount = React.useCallback(async () => {
        const activeAccount = ensureActiveAccount();
        const accountsToValidate = activeAccount ? [activeAccount] : instance.getAllAccounts();

        if (accountsToValidate.length === 0) {
            return false;
        }

        try {
            setIsLoading(true);
            setLoadingMessage('Authenticating');

            const users = await Promise.all(
                accountsToValidate.map(async (accountInfo) => {
                    const authResult = await instance.acquireTokenSilent({
                        account: accountInfo,
                        scopes: request.scopes
                    });

                    return {
                        name: accountInfo.name,
                        username: accountInfo.username,
                        idToken: authResult.idToken
                    };
                })
            );

            const response = await CustomerService.send('POST', users, 'Customer/validate');

            if (response !== null) {
                if (response.success) {
                    localStorage.setItem("CustomerToken", response.token);
                    if (response.customer !== null) {
                        let user = response.customer;
                        setLoggedInUser(user);

                        const params = new URLSearchParams(location.search);
                        if (params.get('login') === 'Y') {
                            console.log('Login parameter is Y');
                            navigate('/Portal');
                        }
                        else if (location.pathname !== '/') {
                            navigate(location.pathname);
                        } else {
                            navigate('/portal');
                        }
                    }
                    return true;
                }
                if (response.reason !== undefined && response.reason === 'expired') {
                    await instance.loginRedirect(request);
                }
                else if (response.reason !== undefined && response.reason === 'Unknown Customer') {
                    let data = {
                        email: response.email
                    };
                    eventEmitter.emit('openAccountRequestDialog', data); // listener in Landing.jsx
                }
                else if (response.message === 'Under Maintenance') {
                    setLocationUnderMaintenance(true);
                }
            }
        } catch (error) {
            if (error?.errorCode === "interaction_required" || error?.name === "InteractionRequiredAuthError") {
                await instance.loginRedirect(request);
            } else {
                enqueueSnackbar(error?.error !== undefined ? error.error : error?.message !== undefined ? error.message : 'An error occurred', { variant: 'error' });
            }
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
        }

        return false;
    }, [ensureActiveAccount, enqueueSnackbar, instance, location.pathname, location.search, navigate, request]);

    React.useEffect(() => {
        let cancelled = false;

        const synchronizeSession = async () => {
            const hasSession = await checkTokenAndLogin();
            if (cancelled || hasSession) {
                return;
            }

            const redirectIdToken = sessionStorage.getItem('msalRedirectIdToken');
            if (redirectIdToken) {
                const createdSession = await generateJWTToken(redirectIdToken);
                sessionStorage.removeItem('msalRedirectIdToken');
                if (cancelled || createdSession) {
                    return;
                }
            }

            await checkAccount();
        };

        synchronizeSession();

        return () => {
            cancelled = true;
        };
    }, [checkAccount, checkTokenAndLogin, generateJWTToken]);

    function handleCloseAccountNotVerified() {
        logoutRequested();
    }

    function logoutRequested() {
        setConfirmDialog(null);
        sessionStorage.clear();
        localStorage.clear();
        setLoggedInUser(null);
        instance.logoutRedirect({
            postLogoutRedirectUri: import.meta.env.VITE_ENTRA_REDIRECTURL,
        });
    }

    // Function to check if the token is expired
    function isTokenExpired(token) {
        try {
            const payloadBase64 = token.split('.')[1]; // JWT payload is the second part
            const decodedPayload = JSON.parse(atob(payloadBase64)); // Decode from Base64

            // exp is in seconds, so we compare with current time in seconds
            const currentTime = Math.floor(Date.now() / 1000);
            return decodedPayload.exp < currentTime;
        } catch (error) {
            console.error("Error checking token expiration:", error);
            return true; // If there's an error, assume token is expired
        }
    }

    useWebSocket(localStorage.getItem('CustomerToken'), 'Customer');

  return (
      <>
      <ErrorBoundary>
          <UserProvider value={{ user: loggedInUser, setUser: setLoggedInUser }}>
            <SnackbarProvider>
              <ThemeProvider theme={theme}>
                  <CssBaseline />
                  {locationUnderMaintenance ?
                    <div>
                        <UnderMaintenance />
                    </div>
                          :
                          <Routes>

                              <Route path="/" element={<Landing logoutRequested={logoutRequested} />} />
                              <Route path="/Signup" element={<Landing />} />
                              <Route path="/Portal" element={<Home />} />
                              <Route path="/Portal/Store" element={<StoreView />} />
                              <Route path="/Portal/Quotes" element={<QuotesView />} />
                          </Routes>
                      }
                      {
                          confirmDialog !== null ?
                              <ConfirmDialog Close={confirmDialog.close} Title={confirmDialog.title} Content={confirmDialog.content} CancelText={confirmDialog.cancelText} AcceptText={confirmDialog.acceptText} Data={confirmDialog.data} />
                              : null
                      }
              </ThemeProvider>
              </SnackbarProvider>
          </UserProvider>
          </ErrorBoundary>
    </>
  )
}

export default App
